import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

/**
 * What the automatic visual review did, in the creator's language.
 *
 * Deliberately one sentence per outcome rather than one "review failed": an
 * auth refusal, a runtime that offers no visual review, a screenshot that
 * never finished and a transport error are different facts with different
 * remedies. Two of these were added after the browser showed the screen
 * blaming the runtime for a capture that had failed while the runtime was
 * advertising the skill — a sentence naming the wrong cause is worse than
 * none.
 */
export interface PreviewReviewCopy {
  reviewed: string
  unauthorized: string
  unavailable: string
  captureFailed: string
  surfaceNotRendered: string
  failed: (status: number) => string
}

export const EN_PREVIEW_REVIEW_COPY: PreviewReviewCopy = {
  reviewed: 'Visual review done for this screen.',
  unauthorized: 'Visual review was refused — this session is not signed in for it.',
  unavailable: 'No visual review available — this runtime does not offer one.',
  captureFailed: 'Visual review stopped — this screen could not be captured.',
  surfaceNotRendered: 'Visual review skipped — this screen was not on display yet.',
  failed: (status) => `Visual review could not be sent (${status}).`,
}

export const KO_PREVIEW_REVIEW_COPY: PreviewReviewCopy = {
  reviewed: '이 화면 시각 검토를 마쳤습니다.',
  unauthorized: '시각 검토가 거부됐습니다 — 이 세션에 권한이 없습니다.',
  unavailable: '시각 검토를 할 수 없습니다 — 이 런타임이 제공하지 않습니다.',
  captureFailed: '시각 검토를 멈췄습니다 — 이 화면을 캡처하지 못했습니다.',
  surfaceNotRendered: '시각 검토를 건너뛰었습니다 — 이 화면이 아직 표시되지 않았습니다.',
  failed: (status) => `시각 검토를 보내지 못했습니다 (${status}).`,
}

const JA_PREVIEW_REVIEW_COPY: PreviewReviewCopy = {
  reviewed: 'この画面のビジュアル検証が完了しました。',
  unauthorized: 'ビジュアル検証が拒否されました — このセッションに権限がありません。',
  unavailable: 'ビジュアル検証を利用できません — このランタイムは提供していません。',
  captureFailed: 'ビジュアル検証を中止しました — この画面をキャプチャできませんでした。',
  surfaceNotRendered: 'ビジュアル検証をスキップしました — この画面がまだ表示されていません。',
  failed: (status) => `ビジュアル検証を送信できませんでした (${status})。`,
}

const ZH_PREVIEW_REVIEW_COPY: PreviewReviewCopy = {
  reviewed: '此页面的视觉检查已完成。',
  unauthorized: '视觉检查被拒绝 — 此会话没有权限。',
  unavailable: '无法进行视觉检查 — 此运行时未提供。',
  captureFailed: '已停止视觉检查 — 无法截取此页面。',
  surfaceNotRendered: '已跳过视觉检查 — 此页面尚未显示。',
  failed: (status) => `无法发送视觉检查 (${status})。`,
}

const ZH_TW_PREVIEW_REVIEW_COPY: PreviewReviewCopy = {
  reviewed: '此頁面的視覺檢查已完成。',
  unauthorized: '視覺檢查遭拒 — 此工作階段沒有權限。',
  unavailable: '無法進行視覺檢查 — 此執行環境未提供。',
  captureFailed: '已停止視覺檢查 — 無法擷取此頁面。',
  surfaceNotRendered: '已跳過視覺檢查 — 此頁面尚未顯示。',
  failed: (status) => `無法傳送視覺檢查 (${status})。`,
}

/** Typed per locale, so the compiler is the key-parity gate. */
export const PREVIEW_REVIEW_COPY: Record<LocaleCode, PreviewReviewCopy> = {
  en: EN_PREVIEW_REVIEW_COPY,
  ko: KO_PREVIEW_REVIEW_COPY,
  ja: JA_PREVIEW_REVIEW_COPY,
  zh: ZH_PREVIEW_REVIEW_COPY,
  'zh-TW': ZH_TW_PREVIEW_REVIEW_COPY,
}
